import React, { useState, useEffect } from 'react';
import { Button } from '@workboard/wobo-ui';
import AddIcon from '@material-ui/icons/AddCircleOutline';
import RemoveIcon from '@material-ui/icons/RemoveCircleOutline';
import { useTranslation } from 'react-i18next';
import { Backdrop, Box, CircularProgress, Tooltip } from '@mui/material';
import { KpiService } from '../../../apps/financials/services/kpi.service';
import {
  FinancialsConfigureModal,
  FinancialsService,
  FinancialsObjectType,
  ExcelIntegrationModal,
  FinancialType,
  WOBO_APP_IDS,
  resyncFinancialIntegration,
  trackFinancialsEvent,
  hasExcelIntegrationDataChanged,
  FINANCIALS_ACTIONS,
  FINANCIALS_LOCATIONS,
  FINANCIALS_SOURCES,
  getObjectFromObjectType,
} from '../../../apps/financials';
import type { FinancialsConfigureState } from '../../../apps/financials/services/financials.service';
import { krdetailConstants } from '../../../constants/krdetails.constants';
import { transformDataPointValues } from '../../../apps/financials/helpers/transformDataPointValues';
import { useClosedKrLock } from './closedKrLock.context';

const SAVE_FIRST_TOOLTIP = 'Must save Objective|KeyResult before adding a financial target';

const ToggleFinancialsButton = ({
  isDisabled,
  onClick,
  hasEntity,
}: {
  // eslint-disable-next-line react/require-default-props
  isDisabled?: boolean;
  onClick: () => void;
  // eslint-disable-next-line react/require-default-props
  hasEntity?: boolean;
}) => {
  const { t } = useTranslation();
  return (
    <Button
      style={{
        padding: '4px 4px 4px 0px',
        fontSize: '13px',
        fontWeight: 700,
      }}
      kind="normal"
      data-testid="financial-targets-button"
      isDisabled={isDisabled}
      className={`krAlign-label${isDisabled ? ' Mui-disabled' : ''}`}
      onClick={onClick}
      variant="ghost"
      startIcon={
        hasEntity ? (
          <RemoveIcon
            style={{
              cursor: isDisabled ? 'not-allowed' : 'pointer',
              width: '18px',
              color: '#596373',
              fontWeight: 700,
              margin: '0px 0px 0px 4px',
            }}
          />
        ) : (
          <AddIcon
            style={{
              cursor: isDisabled ? 'not-allowed' : 'pointer',
              width: '18px',
              color: '#596373',
              fontWeight: 700,
              margin: '0px 0px 0px 4px',
            }}
          />
        )
      }
    >
      {hasEntity ? t('Edit Financial Targets') : t('Financial Targets')}
    </Button>
  );
};

const ExcelIcon = () => (
  <span
    className="icon icon-excel-logo-14px"
    style={{
      display: 'inline-block',
      width: 18,
      height: 18,
      transform: 'scale(1.25)',
    }}
  />
);

const EXCEL_INTEGRATION_TYPE_ID = 23;

export const AddFinancialTargetsButton = ({
  objectType,
  objectId: objectIdStringOrNumber,
  ownerUserId: ownerUserIdStringOrNumber,
  isDisabled = false,
}: {
  objectType: 'okr' | 'kr';
  objectId: number | undefined | null;
  ownerUserId?: number | string;
  isDisabled?: boolean;
}) => {
  const objectId = Number(objectIdStringOrNumber);
  const ownerUserId = Number(ownerUserIdStringOrNumber);
  const isFinancialsEnabled =
    typeof window !== 'undefined' && (window as { wbConfig?: { isFinancialsEnabled?: boolean } }).wbConfig?.isFinancialsEnabled;
  const { t } = useTranslation();
  const isClosedKr = useClosedKrLock();
  const [showFinancialsConfigureModal, setShowFinancialsConfigureModal] = useState(false);
  const [showExcelIntegrationModal, setShowExcelIntegrationModal] = useState(false);
  const [kpiIntegrationData, setKpiIntegrationData] = useState<KpiData | null>(null);
  const [entityId, setEntityId] = useState<number | null>(null);
  const [
    financialsConfigureState,
    setFinancialsConfigureState,
  ] = useState<FinancialsConfigureState | undefined>(
    undefined,
  );
  const [
    externalCostBudgetResources,
    setExternalCostBudgetResources,
  ] = useState<{
    budget?: number;
    resources?: number;
    runningCost?: number;
  } | undefined>(undefined);
  const [refreshTrigger, _setRefreshTrigger] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);

  useEffect(() => {
    if (!isFinancialsEnabled || !objectId || entityId || isDisabled) return;
    const financialsService = new FinancialsService();

    const getIntegrationInfo = async (financialEntityId: number) =>
      KpiService.getIntegrationInfoForObject({
        integrationType: krdetailConstants.EXCEL_ID,
        objectType: 'financial',
        objectId: financialEntityId,
      });

    const fetchFinancialEntity = async () => {
      const response = await financialsService.getEntityByObject({
        objectType: objectType === 'okr' ? FinancialsObjectType.OKR : FinancialsObjectType.KR,
        objectId,
      });
      const financialEntityId = response?.data?.financial?.id;
      if (financialEntityId) {
        setEntityId(financialEntityId);
        const integrationInfo = await getIntegrationInfo(financialEntityId);
        setKpiIntegrationData(integrationInfo);
      } else {
        setEntityId(null);
        setKpiIntegrationData(null);
      }
    };

    fetchFinancialEntity();
  }, [
    objectType,
    objectId,
    showFinancialsConfigureModal,
    isFinancialsEnabled,
    isDisabled,
    entityId,
    refreshTrigger,
  ]);

  const refresh = () => {
    setTimeout(() => {
      _setRefreshTrigger((prev) => prev + 1);
    });
  };

  // hide button if financials is not enabled or objectId is not set
  if (!isFinancialsEnabled || !objectId) return null;

  const button = (
    <ToggleFinancialsButton
      isDisabled={isDisabled || isClosedKr}
      onClick={() => objectId && setShowFinancialsConfigureModal((showModal) => !showModal)}
      hasEntity={!!entityId}
    />
  );
  const modalZIndex = 99999999;

  return (
    <div style={{ display: 'flex', flexDirection: 'row', padding: '12px 0px' }}>
      {isDisabled ? (
        <Tooltip title={SAVE_FIRST_TOOLTIP}>
          <span style={{ display: 'inline-flex' }}>{button}</span>
        </Tooltip>
      ) : (
        button
      )}
      {showFinancialsConfigureModal && objectId && !showExcelIntegrationModal && (
        <FinancialsConfigureModal
          initialDataProviderIds={ownerUserId != null ? [Number(ownerUserId)] : undefined}
          objectId={objectId}
          objectType={objectType === 'okr' ? 1 : 2}
          entityId={entityId}
          open={showFinancialsConfigureModal}
          initialValues={financialsConfigureState}
          // only support excel for now
          externalSourceId={kpiIntegrationData ? WOBO_APP_IDS.EXCEL : undefined}
          externalCostBudgetResources={externalCostBudgetResources}
          onClose={() => setShowFinancialsConfigureModal(false)}
          onSave={() => {
            setShowFinancialsConfigureModal(false);
          }}
          onTrackEvent={window.wb_track}
          trackingLocation={FINANCIALS_LOCATIONS.OKR_WIZARD}
          onDelete={() => {
            if (kpiIntegrationData?._id) {
              KpiService.deactivateIntegration([kpiIntegrationData._id]);
            }
            setShowFinancialsConfigureModal(false);
            setEntityId(null);
            setKpiIntegrationData(null);
            setExternalCostBudgetResources(undefined);
          }}
          baseUrl=""
          sx={{
            zIndex: modalZIndex,
          }}
          renderChildren={(props) => (
            <Box sx={{ position: 'relative', marginTop: '12px' }}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  onClick={() => {
                    setShowExcelIntegrationModal(true);
                    setFinancialsConfigureState(
                      (props as unknown as { state: FinancialsConfigureState })?.state,
                    );
                  }}
                  style={{
                    padding: '16px',
                    justifyContent: 'flex-start',
                    width: 'max-content',
                    fontSize: '13px',
                    fontWeight: 700,
                    borderRadius: '4px',
                    border: '0.5px solid #0072AB',
                    color: '#0072AB',
                  }}
                  startIcon={<ExcelIcon />}
                >
                  {kpiIntegrationData ? t('Modify data connections') : t('Automate values from external source')}
                </Button>
                {kpiIntegrationData && (
                  <Button
                    onClick={async () => {
                      if (!entityId || isSyncing) return;
                      setIsSyncing(true);
                      const financialsService = new FinancialsService();
                      await financialsService.resyncIntegration(entityId);
                      (props as { onRefetch?: () => void })?.onRefetch?.();
                      setExternalCostBudgetResources((prev) => (prev ? { ...prev } : {}));
                      refresh();
                      setIsSyncing(false);
                    }}
                    disabled={isSyncing}
                    style={{
                      padding: '16px',
                      justifyContent: 'flex-start',
                      width: 'max-content',
                      fontSize: '13px',
                      fontWeight: 700,
                      borderRadius: '4px',
                      border: '0.5px solid #0072AB',
                      color: '#0072AB',
                    }}
                  >
                    {t('Sync data from connection')}
                  </Button>
                )}
              </Box>
              <Backdrop open={isSyncing} sx={{ zIndex: modalZIndex + 1 }}>
                <CircularProgress color="inherit" />
              </Backdrop>
            </Box>
          )}
        />
      )}
      {showExcelIntegrationModal && (
        <ExcelIntegrationModal
          open={showExcelIntegrationModal}
          onClose={() => setShowExcelIntegrationModal(false)}
          onSave={async (data: any) => {
            setKpiIntegrationData(data?.kpiData);
            if (entityId) {
              try {
                await resyncFinancialIntegration(entityId, () => {
                  refresh();
                });
                if (hasExcelIntegrationDataChanged(kpiIntegrationData, data?.kpiData)) {
                  trackFinancialsEvent(FINANCIALS_ACTIONS.EDIT_FINANCIALS, {
                    location: FINANCIALS_LOCATIONS.OKR_WIZARD,
                    object: getObjectFromObjectType(
                      objectType === 'okr' ? FinancialsObjectType.OKR : FinancialsObjectType.KR,
                    ),
                    source: FINANCIALS_SOURCES.EXCEL,
                  });
                }
              } finally {
                setShowExcelIntegrationModal(false);
              }
            } else {
              setShowExcelIntegrationModal(false);
            }
          }}
          onRemove={() => {
            setKpiIntegrationData(null);
            setShowExcelIntegrationModal(false);
            setExternalCostBudgetResources(undefined);
          }}
          // create financial entity and set external cost budget resources
          getObjectIdAsync={async (dataPointValues: Array<{ id: number; value: number }>) => {
            const hasAnyCellSelected = dataPointValues.some(
              (dp) => dp.value != null,
            );
            if (!hasAnyCellSelected) {
              return null;
            }
            const financialsService = new FinancialsService();
            const externalData = transformDataPointValues(dataPointValues);
            const response = await financialsService.createEntity(
              objectType === 'okr' ? FinancialsObjectType.OKR : FinancialsObjectType.KR,
              objectId,
              {
                ...financialsConfigureState,
                ...externalData,
              },
            );
            const newEntityId = response?.data?.financial?.id;
            if (newEntityId) {
              trackFinancialsEvent(FINANCIALS_ACTIONS.ADD_FINANCIALS, {
                location: FINANCIALS_LOCATIONS.OKR_WIZARD,
                object: getObjectFromObjectType(
                  objectType === 'okr' ? FinancialsObjectType.OKR : FinancialsObjectType.KR,
                ),
                source: FINANCIALS_SOURCES.EXCEL,
              });
            }
            setExternalCostBudgetResources(externalData);
            refresh();
            return newEntityId ?? null;
          }}
          objectId={entityId} // Financials object id
          objectType="financial"
          updateFrequency="daily"
          config={{
            showAdvancedSection: false,
            dataPoints: [
              {
                id: FinancialType.Budget,
                label: t('Budget'),
                optional: true,
              },
              {
                id: FinancialType.Resources,
                label: t('Resources'),
                optional: true,
              },
              {
                id: FinancialType.Cost,
                label: t('Cost'),
                optional: true,
              },
            ],
          }}
        />
      )}
      <style>
        {`
          .MuiModal-root.MuiDialog-root.AppServices-ExcelModal {
            z-index: ${modalZIndex + 1000000};
          }
        `}
      </style>
    </div>
  );
};

AddFinancialTargetsButton.defaultProps = {
  ownerUserId: undefined,
  isDisabled: false,
};
